import React, { useEffect, useState } from 'react';
import { getSignedInUser, loginUrl } from './auth';
import { AppHeader } from './components/AppHeader';
import { HelpModal } from './components/HelpModal';
import { ShortcutsModal } from './components/ShortcutsModal';
import { useLocation } from './navigation';
import { CheckInOutPage } from './pages/CheckInOutPage';
import { RegisterPage } from './pages/RegisterPage';
import './App.css';

// "/" checks an asset in or out. "/register" browses the SharePoint list.
// Tag lookup tries SharePoint first, then Asset Tiger if the tag is missing.
function App() {
  const { path, search } = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showHelp, setShowHelp] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    async function checkAuth() {
      const signedInUser = await getSignedInUser();
      setIsLoading(false);

      if (signedInUser) {
        setUser(signedInUser);
        return;
      }

      // Azure SWA auth is not available during local `npm start`.
      if (process.env.NODE_ENV !== 'production') {
        setUser({ userDetails: 'local.dev@pacgroup.com', userId: 'local-dev' });
        return;
      }

      window.location.href = loginUrl();
    }

    checkAuth();
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === '?' || (event.shiftKey && event.key === '/')) {
        event.preventDefault();
        setShowShortcuts(true);
        return;
      }

      if (event.key === 'Escape') {
        setShowShortcuts(false);
        setShowHelp(false);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (isLoading) {
    return (
      <div className="centered-screen">
        <div className="boot-card">
          <div className="spinner spinner-lg" />
          <div className="loading-text">Loading PAC Asset Portal...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="centered-screen">
        <div className="boot-card">
          <div className="redirect-text">Redirecting to sign in...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`page${path === '/register' ? ' is-register' : ''}`}>
      {!isOnline && (
        <div className="offline-banner">You're offline. Please reconnect to submit.</div>
      )}

      {showShortcuts && <ShortcutsModal onClose={() => setShowShortcuts(false)} />}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      <AppHeader
        user={user}
        currentPath={path}
        onHelp={() => setShowHelp(true)}
        onShortcuts={() => setShowShortcuts(true)}
      />

      {path === '/register' ? (
        <RegisterPage />
      ) : (
        <CheckInOutPage user={user} search={search} isOnline={isOnline} />
      )}
    </div>
  );
}

export default App;
